/* @flow */
import * as React from 'react';
import invariant from 'invariant';
import Modal from 'react-modal';

import Button from 'components/Button.jsx';
import Link from 'components/Link.jsx';
import Text from 'components/Text.jsx';
import Dialog from 'components/Dialog.jsx';
import type { PeopleListRow } from '../PeopleListTableDataContainer.jsx';
import RequestTranscriptModal from 'organizations/components/RequestTranscriptModal.jsx';
import RemoveFromOrgModal from 'organizations/components/RemoveFromOrgModal.jsx';

import './PeopleListBatchActions.scss';

export type Props = {
  users: PeopleListRow[],
  onDeselectAll: () => void,
  associatedOrg: ?Object,
  reloadList: () => void,
};

type State = {
  roleSelectModalOpen: boolean,
  messageHistoriesModalOpen: boolean,
  removeModalOpen: boolean,
};

export default class PeopleListBatchActions extends React.Component<Props, State> {
  state: State = {
    roleSelectModalOpen: false,
    messageHistoriesModalOpen: false,
    removeModalOpen: false,
  };

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  mounted: boolean = false;

  shouldRenderAction = (permission: string) => {
    const { users } = this.props;

    // TODO remove this once this permission field is added
    if (permission === 'canRequestMessageHistory') {
      return true;
    }

    return users.every(u => u.userInteractionPermissions[permission]);
  };

  afterRemovedUsers = () => {
    const { reloadList, onDeselectAll } = this.props;
    reloadList();
    onDeselectAll();
  };

  openChangeRoleModal = () => {
    this.setState({
      roleSelectModalOpen: true,
    });
  };

  closeChangeRoleModal = () => {
    if (this.mounted) {
      this.setState({
        roleSelectModalOpen: false,
      });
    }
  };

  openGetMessageHistoriesModal = () => {
    this.setState({
      messageHistoriesModalOpen: true,
    });
  };

  closeGetMessageHistoriesModal = () => {
    if (this.mounted) {
      this.setState({
        messageHistoriesModalOpen: false,
      });
    }
  };

  openRemoveUsersModal = () => {
    this.setState({
      removeModalOpen: true,
    });
  };

  closeRemoveUsersModal = () => {
    if (this.mounted) {
      this.setState({
        removeModalOpen: false,
      });
    }
  };

  renderMessageHistoriesModal(onRequestHide: () => void) {
    const { associatedOrg, users } = this.props;

    invariant(associatedOrg, 'PeopleListBatchActions renderMessageHistoriesModal no orgId given');

    // TODO: add these permissions to the payload coming back
    // const canGetMessageHistory = u.userInteractionPermissions['canGetMessageHistory'];
    // const canRequestMessageHistory = u.userInteractionPermissions['canRequestMessageHistory'];

    const canGetMessageHistory = true;
    const canRequestMessageHistory = true;

    const formattedUsers = users.map(u => {
      return {
        id: u.user.id,
        fullName: u.user.name,
        firstName: u.user.firstName,
        canGetMessageHistory,
        canRequestMessageHistory,
      };
    });

    return (
      <RequestTranscriptModal
        onRequestHide={onRequestHide}
        users={formattedUsers}
        organizationId={associatedOrg.id}
      />
    );
  }

  renderRemoveFromOrgModal = (onRequestHide: () => void) => {
    const { users, associatedOrg } = this.props;
    const userIds = users.map(u => u.user.id);

    invariant(associatedOrg, 'PeopleListBatchActions renderRemoveFromOrgModal without an org');

    return (
      <RemoveFromOrgModal
        onRequestHide={onRequestHide}
        isAdminView
        userIds={userIds}
        orgId={associatedOrg.id}
        onRemoved={this.afterRemovedUsers}
        orgName={associatedOrg.name}
      />
    );
  };

  render() {
    const { onDeselectAll, users } = this.props;
    const { roleSelectModalOpen, messageHistoriesModalOpen, removeModalOpen } = this.state;

    return (
      <div className="PeopleListBatchActions">
        <div className="PeopleListBatchActions-deselect">
          <Button level="secondary" weight="semibold" onClick={onDeselectAll}>
            Deselect ({users.length})
          </Button>
        </div>
        <div className="PeopleListBatchActions-actions">
          <Text size="xxxsmall" uppercase weight="semibold">
            Actions
          </Text>
          <div>
            <Link
              color="brand"
              block
              weight="semibold"
              onClick={this.openChangeRoleModal}
              disabled={!this.shouldRenderAction('canChangeRole')}>
              Change role
              <Text weight="normal" size="small" color="pitch">
                Apply new roles to selected users.
              </Text>
            </Link>
          </div>
          <div>
            <Link
              color="brand"
              block
              weight="semibold"
              onClick={this.openGetMessageHistoriesModal}
              disabled={!this.shouldRenderAction('canRequestMessageHistory')}>
              Get message histories
              <Text weight="normal" size="small" color="pitch">
                Get the complete message histories of the selected users.
              </Text>
            </Link>
          </div>
          <div>
            <Link
              color="brand"
              block
              weight="semibold"
              onClick={this.openRemoveUsersModal}
              disabled={!this.shouldRenderAction('canRemoveFromGroup')}>
              Remove
              <Text weight="normal" size="small" color="pitch">
                Remove these users from the organization.
              </Text>
            </Link>
          </div>
        </div>
        <Modal isOpen={roleSelectModalOpen}>
          <Dialog onRequestHide={this.closeChangeRoleModal}>TODO</Dialog>
        </Modal>
        <Modal isOpen={messageHistoriesModalOpen}>
          {this.renderMessageHistoriesModal(this.closeGetMessageHistoriesModal)}
        </Modal>
        <Modal isOpen={removeModalOpen}>
          {this.renderRemoveFromOrgModal(this.closeRemoveUsersModal)}
        </Modal>
      </div>
    );
  }
}
